import { Injectable } from '@angular/core';
// import { ActivatedRoute, Params, Router } from '@angular/router';
// import { Observable } from 'rxjs';
import {
  ActivatedRoute,
  NavigationStart,
  Params,
  Router,
  Event,
  NavigationEnd,
  NavigationCancel,
  RouterStateSnapshot,
} from '@angular/router';
import { Observable, BehaviorSubject, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';

@Injectable()
export class RoutingService {
  isNavigationFinished$: Observable<boolean>;
  installationIdWithModifiedHistory$: BehaviorSubject<number> = new BehaviorSubject(null);

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  // Should be called only once - can't be called in constructor because it breaks unit tests in other files
  setIsNavigationFinished(): void {
    if (this.isNavigationFinished$) {
      return;
    }

    const navigationStart$: Observable<NavigationStart> = this.router.events.pipe(
      filter((e: Event): e is NavigationStart => e instanceof NavigationStart)
    );
    const navigationEndOrCancel$: Observable<
      NavigationEnd | NavigationCancel
    > = this.router.events.pipe(
      filter(
        (e: Event): e is NavigationEnd | NavigationEnd =>
          e instanceof NavigationEnd || e instanceof NavigationCancel
      )
    );
    const isNavigationFinished$: Observable<boolean> = merge(
      navigationStart$.pipe(map(() => false)),
      navigationEndOrCancel$.pipe(map(() => true))
    ).pipe(distinctUntilChanged(), shareReplay());
    this.isNavigationFinished$ = isNavigationFinished$;
    isNavigationFinished$.subscribe(); // Observer needed to emit values
  }

  addChildPathToUrl(path: string, queryParams?: Params): void {
    const url = this.router.url.split('?')[0];

    if (path) {
      this.router.navigate([url + '/' + path], {
        queryParams,
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  replaceChildPathOnUrl(path: string, queryParams?: Params): void {
    const url = this.router.url.split('?')[0];
    const urlSegments = url.split('/');
    const urlWithoutLastSegment = urlSegments.slice(0, urlSegments.length - 1).join('/');

    if (path) {
      this.router.navigate([urlWithoutLastSegment + '/' + path], {
        queryParams,
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }
  dropChildPathFromUrl(removeDeviceSerialParam = false, gatewaySerial?: string): void {
    const url = this.router.url.split('?')[0];
    const urlSegments = [...url.split('/'), this.router.url.split('?')[1]];
    const installationIndex = urlSegments.indexOf('installations');
    const urlWithoutLastSegment =
      installationIndex !== -1
        ? `/${urlSegments[installationIndex]}/${urlSegments[installationIndex + 1]}`
        : url;
    const queryParams: Params = { deviceSerial: null };
    if (gatewaySerial) {
      queryParams.gatewaySerial = gatewaySerial;
    }

    this.router.navigate([urlWithoutLastSegment], {
      relativeTo: this.activatedRoute,
      ...(removeDeviceSerialParam ? { queryParams } : {}),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  updateUrl(queryParams: Record<string, string | number>): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  changeChildInGuard(currentUrl: string, expectedChildRoute: string): void {
    let queryParams = currentUrl.split('?')[1];
    if (queryParams) {
      queryParams = '?' + queryParams;
    }

    const urlWithChangedChild = currentUrl.split('?')[0].split('/');
    urlWithChangedChild[urlWithChangedChild.length - 1] = expectedChildRoute;

    this.updateCachedInstallationId(currentUrl);

    this.replaceChildPathOnUrl(urlWithChangedChild.join('/') + queryParams);
  }

  navigateToInstallationDashboardFromGuard(routerStateSnapshot: RouterStateSnapshot): void {
    const installationDetailsUrl = routerStateSnapshot.url.split('/').slice(0, 3).join('/');

    // Prevents url flickering - query param would be added by effect
    const deviceSerialQueryParam = routerStateSnapshot.root.queryParams.gatewaySerial;

    this.updateCachedInstallationId(routerStateSnapshot.url);

    this.router.navigate([installationDetailsUrl], {
      relativeTo: this.activatedRoute,
      ...(deviceSerialQueryParam ? { queryParams: { gatewaySerial: deviceSerialQueryParam } } : {}),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // Prevents fetching data triggered by basic guard when url is changed
  private updateCachedInstallationId(url: string): void {
    const installationId = url.split('/').slice(0, 3)[2];
    this.installationIdWithModifiedHistory$.next(+installationId);
  }

  removeQueryParams(parameters: string[]): void {
    const queryParams: Record<string, null> = {};
    parameters.forEach((param) => {
      queryParams[param] = null;
    });

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
